import { eventChannel, EventChannel, Task } from 'redux-saga';
import {
  all,
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  take,
} from 'redux-saga/effects';

import * as Types from '../actions';
import {
  attachPlugin,
  createSession,
  detachPlugin,
  iceRestart,
  joinRoom,
  listRoomParticipants,
  toggleMuteAudio,
  toggleMuteVideo,
  toggleScreenSharingSuccess,
} from '../actionCreators';
import { StoreState } from '../reducers';

function createNetworkStatusChannel(): EventChannel<boolean> {
  return eventChannel((emitter) => {
    function statusChangeHandler() {
      emitter(navigator.onLine);
    }
    window.addEventListener('online', statusChangeHandler);
    window.addEventListener('offline', statusChangeHandler);
    return () => {
      window.removeEventListener('online', statusChangeHandler);
      window.removeEventListener('offline', statusChangeHandler);
    };
  });
}

function ping() {
  return new Promise<boolean>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.timeout = 2000;
    xhr.open('GET', window.location.origin);
    xhr.onload = () => resolve(true);
    xhr.onerror = () => reject(false);
    xhr.ontimeout = () => reject(false);
    try {
      xhr.send(null);
    } catch (e) {
      reject(false);
    }
  });
}

function* waitForOnline() {
  yield delay(400);
  if (
    ['android', 'iphone', 'ipad'].includes(navigator.platform.toLowerCase())
  ) {
    let online = false;
    while (!online) {
      try {
        yield delay(3000);
        online = yield call(ping);
      } catch (e) {
        online = false;
      }
    }
  } else {
    let online = navigator.onLine;
    while (!online) {
      const channel = (yield call(
        createNetworkStatusChannel
      )) as EventChannel<boolean>;
      online = yield take(channel);
      channel.close();
    }
  }
}

function* handleLostConnection() {
  try {
    const {
      client,
      muteAudio,
      muteVideo,
      screenshare,
      userId,
    }: {
      client: QBVideoConferencingClient | undefined;
      muteAudio: boolean;
      muteVideo: boolean;
      screenshare: boolean;
      userId: number;
    } = yield select(({ auth, call, qb }: StoreState) => ({
      client: qb.client,
      muteAudio: call.muteAudio,
      muteVideo: call.muteVideo,
      screenshare: call.screenshare,
      userId: auth.user ? auth.user.id : -1,
    }));
    if (!client) {
      throw new Error(
        'Failed to recover call: there is no VideoConferencingPlugin instance'
      );
    }
    const roomId = client.currentRoomId;
    if (!roomId) {
      throw new Error('Conferencing client does not have "currentRoomId"');
    }
    yield call(waitForOnline);
    yield put(detachPlugin());
    yield take([
      Types.CLIENT_DETACH_PLUGIN_FAILURE,
      Types.CLIENT_DETACH_PLUGIN_SUCCESS,
    ]);
    yield put(createSession());
    yield take(Types.CLIENT_CREATE_SESSION_SUCCESS);
    yield put(attachPlugin({ isRemote: false, userId: null }));
    yield take(Types.CLIENT_ATTACH_PLUGIN_SUCCESS);
    yield put(joinRoom({ roomId }));
    yield take(Types.CLIENT_JOIN_SUCCESS);
    if (muteAudio || muteVideo) {
      yield fork(function* () {
        yield take(Types.LOCAL_STREAM);
        if (muteAudio) {
          yield put(toggleMuteAudio());
        }
        if (muteVideo) {
          yield put(toggleMuteVideo());
        }
      });
    }
    if (screenshare) {
      yield put(toggleScreenSharingSuccess(false));
    }
    yield put(listRoomParticipants());
    const participantsAction: Types.ListRoomParticipantsSuccessAction = yield take(
      Types.LIST_PARTICIPANTS_SUCCESS
    );
    const publishers = participantsAction.payload.filter(
      (participant) => participant.id !== userId && participant.publisher
    );
    yield all(
      publishers.map((participant) =>
        put(
          attachPlugin({
            isRemote: true,
            userId: participant.id,
          })
        )
      )
    );
  } catch (e) {
    yield put({ type: 'HANDLE_LOST_CONNECTION_ERROR', error: e.message || e });
  }
}

function* restartIceForUser(userId?: number) {
  // give a try to ICE transport to connect
  yield delay(8000);
  yield call(waitForOnline);
  // give a try to ICE transport to connect
  yield delay(8000);
  yield put(iceRestart({ isRemote: Boolean(userId), userId }));
}

function* handleIceStateChange() {
  const queue: { [userId: number]: Task | undefined } = {};
  while (true) {
    const action:
      | Types.VideoRoomPluginAction
      | Types.ConferencingClientError = yield take([
      'iceState',
      Types.CONFERENCING_CLIENT_ERROR,
    ]);
    if (action.type === Types.CONFERENCING_CLIENT_ERROR) {
      Object.keys(queue)
        .map(Number)
        .forEach(function* (userId) {
          const task = queue[userId];
          if (task) {
            yield cancel(task);
            queue[userId] = undefined;
          }
        });
    } else {
      const { state, userId } = action.payload;
      const id = userId ? userId : 0;
      const task = queue[id];
      if (task) {
        yield cancel(task);
      }
      if (['disconnected', 'failed'].includes(state)) {
        queue[id] = yield fork(restartIceForUser, userId);
      }
    }
  }
}

function* handleConferencingClientErrors() {
  let lostConnectionHandlerTask: Task | undefined = undefined;
  while (true) {
    const action: Types.ConferencingClientError = yield take(
      Types.CONFERENCING_CLIENT_ERROR
    );
    const { error } = action;
    if (
      typeof error === 'string' &&
      error.toLowerCase().includes('lost connection')
    ) {
      // websocket closed, create new session
      if (
        !lostConnectionHandlerTask ||
        !lostConnectionHandlerTask.isRunning()
      ) {
        lostConnectionHandlerTask = yield fork(handleLostConnection);
      }
    } else if (
      typeof error === 'string' &&
      error.toLowerCase().includes('is the server down?')
    ) {
      if (lostConnectionHandlerTask) {
        yield cancel(lostConnectionHandlerTask);
      }
      lostConnectionHandlerTask = yield fork(handleLostConnection);
    }
  }
}

export default [handleIceStateChange(), handleConferencingClientErrors()];
